import { OptimizerRunV2Type } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { TFunction } from 'i18next';
import type { RunSituationCount } from '../../types/advisorInsights.types';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type {
  RebalanceInsightCashRunSummary,
  RebalanceInsightRunSituationSummary,
} from '../../types/insightNarrative.types';
import type { BulkRebalanceRun } from '../../types/run.types';
import {
  getReportClientLabel,
  getReportGoalLabel,
} from '../presentation/reportLabels';
import { OptimizerDecisionStage } from './advisorSituationKeys';
import { takeFirst } from './compactInsightAiContext';
import {
  isZeroDeployCashModeApplication,
  resolveAdvisorSituationKey,
} from './formatAdvisorInsightText';
import {
  formatRunSituationExampleSummaryForAi,
  scoreRunSituationExampleSummary,
} from './formatRunSituationExampleSummaryForAi';
import {
  isCashDeployAction,
  isCashRaiseAction,
} from './optimizerDecisionActionPatterns';

export const AI_RUN_SITUATION_SUMMARY_CAP = 10;
export const AI_RUN_SITUATION_EXAMPLE_MAX_CHARS = 500;

const truncateExample = (summary: string): string => {
  if (summary.length <= AI_RUN_SITUATION_EXAMPLE_MAX_CHARS) {
    return summary;
  }

  return `${summary.slice(0, AI_RUN_SITUATION_EXAMPLE_MAX_CHARS - 1).trimEnd()}…`;
};

const resolvePortfolioLabel = (
  audit: OptimizerAuditV2Record,
  reportsById: ReadonlyMap<string, PortfolioReport>,
): string | undefined => {
  const report = audit.portfolioReportId
    ? reportsById.get(audit.portfolioReportId)
    : undefined;

  if (!report) {
    return undefined;
  }

  const label = [getReportClientLabel(report), getReportGoalLabel(report)]
    .filter(Boolean)
    .join(' · ');

  return label || undefined;
};

const findExampleSummaryForStage = (
  stage: string,
  audits: OptimizerAuditV2Record[],
  reportsById: ReadonlyMap<string, PortfolioReport>,
  t: TFunction,
): string | undefined => {
  let bestSummary: string | undefined;
  let bestScore = -1;

  for (const audit of audits) {
    if (audit.passed === false) {
      continue;
    }

    for (const decision of audit.decisions ?? []) {
      if (resolveAdvisorSituationKey(decision) !== stage) {
        continue;
      }

      const summary = formatRunSituationExampleSummaryForAi(stage, audit, t, {
        portfolioLabel: resolvePortfolioLabel(audit, reportsById),
      });

      if (!summary) {
        continue;
      }

      const score = scoreRunSituationExampleSummary(summary);
      if (score > bestScore) {
        bestSummary = summary;
        bestScore = score;
      }
    }
  }

  return bestSummary ? truncateExample(bestSummary) : undefined;
};

export const buildTopSituationSummariesForAi = ({
  situationCounts,
  audits,
  reports = [],
  t,
}: {
  situationCounts: RunSituationCount[];
  audits: OptimizerAuditV2Record[];
  reports?: PortfolioReport[];
  t: TFunction;
}): RebalanceInsightRunSituationSummary[] => {
  const reportsById = new Map(reports.map((report) => [report.id, report]));

  return takeFirst(
    situationCounts.map(({ stage, portfolioCount }) => {
      const exampleSummary = findExampleSummaryForStage(
        stage,
        audits,
        reportsById,
        t,
      );

      return {
        stage,
        portfolioCount,
        ...(exampleSummary ? { exampleSummary } : {}),
      };
    }),
    AI_RUN_SITUATION_SUMMARY_CAP,
  );
};

type CashRunBucket = 'raise' | 'deploy' | 'undeployableDeploy';

const classifyCashRunAudit = (
  audit: OptimizerAuditV2Record,
): CashRunBucket | null => {
  if (audit.passed === false) {
    return null;
  }

  const rebalanceModeDecision = (audit.decisions ?? []).find(
    (decision) => decision.stage === OptimizerDecisionStage.RebalanceMode,
  );

  if (
    rebalanceModeDecision &&
    isZeroDeployCashModeApplication(rebalanceModeDecision.action)
  ) {
    return 'undeployableDeploy';
  }

  const effectiveMode =
    audit.effectiveRebalanceMode ?? audit.rebalanceMode ?? undefined;

  if (effectiveMode === 'RAISE_CASH') {
    return 'raise';
  }

  if (effectiveMode === 'DEPLOY_CASH') {
    return 'deploy';
  }

  if (rebalanceModeDecision) {
    if (isCashRaiseAction(rebalanceModeDecision.action)) {
      return 'raise';
    }

    if (isCashDeployAction(rebalanceModeDecision.action)) {
      return 'deploy';
    }
  }

  return null;
};

export const buildCashRunSummaryForAi = ({
  run,
  audits,
}: {
  run: BulkRebalanceRun;
  audits: OptimizerAuditV2Record[];
}): RebalanceInsightCashRunSummary | undefined => {
  if (run.type !== OptimizerRunV2Type.Transfer) {
    return undefined;
  }

  let raiseCount = 0;
  let deployCount = 0;
  let undeployableDeployCount = 0;

  for (const audit of audits) {
    const bucket = classifyCashRunAudit(audit);
    if (bucket === 'raise') {
      raiseCount += 1;
    } else if (bucket === 'deploy') {
      deployCount += 1;
    } else if (bucket === 'undeployableDeploy') {
      undeployableDeployCount += 1;
    }
  }

  if (raiseCount + deployCount + undeployableDeployCount === 0) {
    return undefined;
  }

  return {
    raiseCount,
    deployCount,
    undeployableDeployCount,
  };
};
